import { useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFieldSalesSummaryHqsBand } from '../hooks/useFieldSalesDashboard.js';
import {
  formatValueAuto,
  formatIndianInt,
  achDelta,
  deltaClass,
  DELTA_COLOR,
  FS_HEAD_SX,
  FS_CELL_SX,
} from './fieldSalesFormat.js';

const NAVY = '#0F1E3D';
const MUTED = '#6B7280';
const BORDER = '#E4E8EE';

// Group display names (reference SPREAD.groupNames).
const GROUP_NAMES = { growth: 'Growth', achievement: 'Achievement', pcpm: 'PCPM' };

// Full band labels per group (reference SPREAD.labels).
const BAND_LABELS = {
  achievement: {
    gt100: '>100% of target',
    b95: '95–100% of target',
    b90: '90–95% of target',
    lt90: '<90% of target',
    no: 'No target set',
  },
  growth: {
    high: '> +10% YoY',
    grow: '0 – +10% YoY',
    slight: '-10 – 0% YoY',
    sharp: '< -10% YoY',
    no: 'No prior-year base',
  },
  pcpm: {
    gt150: '>150% of division PCPM/TM',
    b100: '100–150% of division PCPM/TM',
    b50: '50–100% of division PCPM/TM',
    lt50: '<50% of division PCPM/TM',
  },
};

/** ₹ Cr/L (lakh input), em dash for non-positive. */
const money = (v) => (Number(v) > 0 ? formatValueAuto(v) : '—');

const NO_DATA_TOOLTIP = 'No data source yet';

/**
 * Greyed Zone cell: the backend currently returns the State in the zone field,
 * so Zone has no real source yet. Shown as a greyed "—" (50% opacity, not
 * clickable) with a "No data source yet" tooltip and a small "No data" tag.
 */
function ZoneCell() {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box
        component="span"
        aria-disabled="true"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          opacity: 0.5,
          cursor: 'not-allowed',
          userSelect: 'none',
        }}
      >
        —
        <Box
          component="span"
          sx={{
            px: '6px',
            py: '1px',
            borderRadius: '999px',
            bgcolor: '#eef1f5',
            border: `1px solid ${BORDER}`,
            color: MUTED,
            fontSize: '9px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '.3px',
            whiteSpace: 'nowrap',
          }}
        >
          No data
        </Box>
      </Box>
    </Tooltip>
  );
}

const HEAD_SX = FS_HEAD_SX;
const CELL_SX = FS_CELL_SX;

/** Sum a numeric field across rows. */
function sumBy(rows, key) {
  return rows.reduce((acc, r) => acc + (Number(r?.[key]) || 0), 0);
}

/**
 * Band drill-down modal (reference "Spread-of-Performance" modal). Loads the
 * HQ rows for one KPI band from /summary/hqs (group + band only) and lists
 * #, HQ, [Division when >1 division], Zone, TMs, Target, Sales, Ach %, Growth %,
 * PCPM/TM with a Total row. Navy header with a ✕; closes on ✕, overlay click
 * and Escape — exactly like the HTML.
 *
 * The header count (N) is the SAME number as the tile/footer that opened the
 * modal (passed as `count`/`unit`), not the number of rows the endpoint
 * returns — so the title always matches what the user clicked.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   descriptor: {
 *     group?: 'growth'|'achievement'|'pcpm', band?: string, pl?: string,
 *     count?: number, unit?: 'HQs'|'TMs',
 *   }|null,
 * }} props
 */
export default function FieldSalesBandDrilldownModal({ open, onClose, descriptor }) {
  const query = useFieldSalesSummaryHqsBand(descriptor, { enabled: open });
  const rows = query.data?.rows ?? [];
  const loading = query.isLoading || query.isFetching;

  // Close on Escape (reference keydown handler).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const group = descriptor?.group;
  const band = descriptor?.band;
  const pl = descriptor?.pl ?? '';
  const groupName = GROUP_NAMES[group] ?? '';
  const bandLabel = (BAND_LABELS[group] && BAND_LABELS[group][band]) || band || '';

  // N and the HQs/TMs word come from the tile/footer that opened the modal, so
  // the title matches exactly what was clicked (not the row count).
  const count = Number.isFinite(Number(descriptor?.count)) ? Number(descriptor.count) : rows.length;
  const unit = descriptor?.unit ?? 'HQs';
  const unitSingular = unit === 'TMs' ? 'TM' : 'HQ';

  // Division column only when the band spans more than one division.
  const multiDiv = new Set(rows.map((r) => r.division)).size > 1;

  // Totals (reference: sum TMs/Target/Sales, recompute Ach from the sums).
  const tTm = sumBy(rows, 'tm');
  const tTgt = sumBy(rows, 'target');
  const tSal = sumBy(rows, 'sales');
  const tAch = tTgt > 0 ? (tSal / tTgt) * 100 : NaN;

  const headTitle = `${pl ? pl + ' · ' : ''}${groupName} ${bandLabel} — ${formatIndianInt(count)} ${
    count === 1 ? unitSingular : unit
  }`;

  return (
    <Box
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: 'rgba(15,30,61,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        p: '24px',
      }}
    >
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(15,30,61,.3)',
          width: 'min(980px, 96vw)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Navy head */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            p: '13px 18px',
            bgcolor: NAVY,
            color: '#fff',
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{headTitle}</Typography>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            sx={{
              background: 'transparent',
              border: 0,
              color: '#cbd3e3',
              fontSize: '18px',
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ✕
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ p: '14px 18px', overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ fontSize: '12px', color: MUTED, py: 1 }}>
              No HQs in this band for the current filters.
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: '66vh', border: `1px solid ${BORDER}`, borderRadius: '10px' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_SX}>#</TableCell>
                    <TableCell sx={HEAD_SX}>HQ</TableCell>
                    {multiDiv ? <TableCell sx={HEAD_SX}>Division</TableCell> : null}
                    <TableCell sx={HEAD_SX}>Zone</TableCell>
                    <TableCell sx={HEAD_SX} align="right">TMs</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Target</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Sales</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Ach %</TableCell>
                    <TableCell sx={HEAD_SX} align="right">Growth %</TableCell>
                    <TableCell sx={HEAD_SX} align="right">PCPM/TM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.hq}-${r.division}-${i}`} hover>
                      <TableCell sx={CELL_SX}>{i + 1}</TableCell>
                      <TableCell sx={{ ...CELL_SX, fontWeight: 600 }}>{r.hq ?? '—'}</TableCell>
                      {multiDiv ? <TableCell sx={CELL_SX}>{r.division ?? '—'}</TableCell> : null}
                      <TableCell sx={CELL_SX}><ZoneCell /></TableCell>
                      <TableCell sx={CELL_SX} align="right">{formatIndianInt(r.tm)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.target)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">{money(r.sales)}</TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        <Box component="span" sx={{ color: DELTA_COLOR[achDelta(r.ach)] }}>
                          {Number.isFinite(Number(r.ach)) ? `${Number(r.ach).toFixed(1)}%` : '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        <Box component="span" sx={{ color: DELTA_COLOR[deltaClass(r.growth)] }}>
                          {Number.isFinite(Number(r.growth))
                            ? `${Number(r.growth) > 0 ? '+' : ''}${Number(r.growth).toFixed(1)}%`
                            : '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={CELL_SX} align="right">
                        {Number.isFinite(Number(r.pcpm_tm)) ? formatValueAuto(r.pcpm_tm) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { bgcolor: '#f8fafc', fontWeight: 700, borderTop: `2px solid ${BORDER}` } }}>
                    <TableCell sx={CELL_SX} colSpan={multiDiv ? 4 : 3}>
                      Total · {formatIndianInt(count)} {count === 1 ? unitSingular : unit}
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right">{formatIndianInt(tTm)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(tTgt)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">{money(tSal)}</TableCell>
                    <TableCell sx={CELL_SX} align="right">
                      <Box component="span" sx={{ color: DELTA_COLOR[achDelta(tAch)] }}>
                        {Number.isFinite(tAch) ? `${tAch.toFixed(1)}%` : '—'}
                      </Box>
                    </TableCell>
                    <TableCell sx={CELL_SX} align="right" />
                    <TableCell sx={CELL_SX} align="right" />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
}
